
import { useState } from 'react';

export interface NotificationsHookReturn {
  permissionGranted: boolean;
  initialize: (requestPermission: boolean) => Promise<boolean>;
  serviceBlocked: boolean;
  serviceWorkerRegistered: boolean;
}

/**
 * Hook for handling browser notifications, simplified version without Firebase
 */
export const useNotifications = (): NotificationsHookReturn => {
  const isBrowser = typeof window !== 'undefined';
  const isNotificationSupported = isBrowser && 'Notification' in window;

  const [permissionGranted, setPermissionGranted] = useState<boolean>(
    isNotificationSupported ? Notification.permission === 'granted' : false
  );
  const [serviceWorkerRegistered, setServiceWorkerRegistered] = useState<boolean>(false);
  const [serviceBlocked, setServiceBlocked] = useState<boolean>(false);

  const initialize = async (requestPermission: boolean = false): Promise<boolean> => {
    try {
      // Check if notifications are supported in this browser
      if (typeof window === 'undefined' || !('Notification' in window)) {
        console.log('This browser does not support notifications');
        setServiceBlocked(true);
        return false;
      }

      // Note: Service worker for Firebase removed
      // Browser notifications still work via native Notification API
      // Background notifications are handled by Supabase Edge Functions

      // If permission is already granted, return true
      if (Notification.permission === 'granted') {
        setPermissionGranted(true);
        return true;
      }

      // If permission is denied, return false and don't ask again
      if (Notification.permission === 'denied') {
        console.log('Notification permission was denied');
        return false;
      }

      // Request permission if specified
      if (requestPermission) {
        const permission = await Notification.requestPermission();
        const granted = permission === 'granted';
        setPermissionGranted(granted);
        return granted;
      }

      return false;
    } catch (error) {
      console.error('Error initializing notifications:', error);
      return false;
    }
  };

  return {
    permissionGranted,
    initialize,
    serviceBlocked,
    serviceWorkerRegistered
  };
};
